import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Search } from 'lucide-react'
import { useSearchTasksQuery } from '../../api/tasks.js'
import { useProjectsQuery } from '../../api/projects.js'
import { useFocusTrap } from '../../lib/use-focus-trap.js'
import { ProjectSwatch } from '../../components/ProjectSwatch.js'

interface SearchModalProps {
  onClose: () => void
}

/** Búsqueda global con `/` (CLAUDE.md §6): título y descripción, en cualquier proyecto. */
export function SearchModal({ onClose }: SearchModalProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dialogRef = useRef<HTMLDivElement>(null)
  const [query, setQuery] = useState('')
  const [debounced, setDebounced] = useState('')
  const projects = useProjectsQuery()
  const results = useSearchTasksQuery(debounced)

  useFocusTrap(dialogRef, onClose)

  const projectsById = useMemo(
    () => new Map((projects.data ?? []).map((project) => [project.id, project] as const)),
    [projects.data],
  )

  useEffect(() => {
    const timeout = setTimeout(() => setDebounced(query), 250)
    return () => clearTimeout(timeout)
  }, [query])

  function openTask(taskId: string, projectId: string): void {
    const project = projectsById.get(projectId)
    if (!project) return
    onClose()
    void navigate(`/projects/${project.key}/tasks/${taskId}`)
  }

  function onSubmit(event: React.FormEvent): void {
    event.preventDefault()
    const first = results.data?.items[0]
    if (first) openTask(first.id, first.projectId)
  }

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 pt-24"
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={t('search.title')}
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
        className="flex max-h-[60vh] w-full max-w-md flex-col rounded-panel bg-surface shadow-lg outline-none"
      >
        <form
          onSubmit={onSubmit}
          className="flex shrink-0 items-center gap-2 border-b border-border px-4 py-3"
        >
          <Search className="h-4 w-4 shrink-0 text-text-muted" aria-hidden="true" />
          <input
            type="text"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t('search.placeholder')}
            className="min-w-0 flex-1 bg-transparent text-body text-text outline-none placeholder:text-text-muted"
          />
        </form>

        <div className="min-h-0 flex-1 overflow-y-auto p-1">
          {debounced.length === 0 && (
            <p className="px-3 py-4 text-meta text-text-muted">{t('search.hint')}</p>
          )}

          {debounced.length > 0 && results.data?.items.length === 0 && (
            <p className="px-3 py-4 text-meta text-text-muted">{t('search.noResults')}</p>
          )}

          {results.data?.items.map((task) => {
            const project = projectsById.get(task.projectId)
            return (
              <button
                key={task.id}
                type="button"
                onClick={() => openTask(task.id, task.projectId)}
                className="flex h-10 w-full items-center gap-2 rounded-card px-2 text-left hover:bg-surface-hi focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
              >
                {project && <ProjectSwatch color={project.color} />}
                <span className="min-w-0 flex-1 truncate text-body text-text">{task.title}</span>
                {project && (
                  <span className="shrink-0 text-meta text-text-muted">{project.key}</span>
                )}
              </button>
            )
          })}
        </div>
      </div>
    </div>,
    document.body,
  )
}
